import React, { useState, useMemo, useEffect } from "react";
import "./incomeTeamTemplate.css";
import { updateIncomeTask } from "../../../../../../firebase/incomeTasks";
// Constants for shared default objects
const EMPTY_CHECK = { checked: false, timestamp: null, user: null };
const EMPTY_FIELD = { value: "", active: true };

const timeToMinutes = (timeStr) => {
  if (!timeStr) return Number.MAX_SAFE_INTEGER;

  const [hours, minutes] = timeStr.split(":").map(Number);
  return (hours * 60) + minutes;
};

// Helper function to check if allocation field is disabled
const isAllocationFieldDisabled = (field, allocationFields) => {
  if (!allocationFields) return false;
  return allocationFields[field] === false;
};

export default function ComponentIncomeTeam({ data }) {
  // Extract data from props
  const { tasks = [], user, country, date } = data || {};
  
  // State for tasks and checkbox popup
  const [localTasks, setLocalTasks] = useState([]);
  const [checkboxPopup, setCheckboxPopup] = useState({
    open: false,
    taskId: null,
    allocationId: null,
    field: "",
    timestamp: "",
    user: ""
  });

  // Update localTasks when tasks prop changes
  useEffect(() => {
    setLocalTasks(tasks || []);
  }, [tasks]);

  /* =====================
     COLUMNS DEFINITION
  ====================== */
  const columns = [
    { name: "Time", width: 70 },
    { name: "Task", width: 400 },
    { name: "Primary", width: 60 },
    { name: "Check", width: 45, checkbox: true },
    { name: "Duality", width: 80 },
    { name: "Check", width: 65, checkbox: true },
    { name: "Items", width: 40 },
    { name: "Short-Name", width: 80 },
    { name: "ISIN", width: 100 },
    { name: "Corp.Ref", width: 100 },
    { name: "ACK", width: 40 },
    { name: "Comments", width: 100 },
    { name: "Actions", width: 60 }
  ];

  /* =====================
     MAP TASKS TO TABLE ROWS
  ====================== */
  const rows = useMemo(() => {
    if (!localTasks || localTasks.length === 0) {
      return [];
    }

    const result = [];

    // ✅ SORT TASKS BY TIME FIRST
    const sortedTasks = [...localTasks].sort((a, b) => {
      return timeToMinutes(a.Time) - timeToMinutes(b.Time);
    });

    sortedTasks.forEach((task, taskIndex) => {
      // Ensure task has required fields
      const safeTask = {
        ...task,
        Time: task.Time || "",
        Task: task.Task || "",
        taskPrimary: task.taskPrimary || EMPTY_FIELD,
        taskPrimaryCheck: task.taskPrimaryCheck || EMPTY_CHECK,
        taskDuality: task.taskDuality || EMPTY_FIELD,
        taskDualityCheck: task.taskDualityCheck || EMPTY_CHECK,
        allocationFields: task.allocationFields || {
          shortName: true,
          isin: true,
          corpRef: true,
          ack: true
        },
        actions: task.actions || { canAdd: true },
        Items: task.Items || "",
        allocations: task.allocations || [],
        Comments: task.Comments || ""
      };

      const hasAllocations = safeTask.allocations.length > 0;

      // If no allocations, create one default row
      const allocations = hasAllocations
        ? safeTask.allocations
        : [
            {
              id: crypto.randomUUID(),
              allocPrimary: EMPTY_FIELD,
              allocPrimaryCheck: EMPTY_CHECK,
              allocDuality: EMPTY_FIELD,
              allocDualityCheck: EMPTY_CHECK,
              shortName: EMPTY_FIELD,
              isin: EMPTY_FIELD,
              corpRef: EMPTY_FIELD,
              ack: EMPTY_FIELD
            }
          ];

      allocations.forEach((alloc, idx) => {
        // Ensure allocation has required fields
        const safeAlloc = {
          ...alloc,
          allocPrimary: alloc.allocPrimary || EMPTY_FIELD,
          allocPrimaryCheck: alloc.allocPrimaryCheck || EMPTY_CHECK,
          allocDuality: alloc.allocDuality || EMPTY_FIELD,
          allocDualityCheck: alloc.allocDualityCheck || EMPTY_CHECK,
          shortName: alloc.shortName || EMPTY_FIELD,
          isin: alloc.isin || EMPTY_FIELD,
          corpRef: alloc.corpRef || EMPTY_FIELD,
          ack: alloc.ack || EMPTY_FIELD
        };

        const row = [
          { value: idx === 0 ? safeTask.Time : "", readonly: idx !== 0 },
          { value: idx === 0 ? safeTask.Task : "", readonly: idx !== 0 },

          idx === 0 ? safeTask.taskPrimary : safeAlloc.allocPrimary,
          idx === 0 ? safeTask.taskPrimaryCheck : safeAlloc.allocPrimaryCheck,
          idx === 0 ? safeTask.taskDuality : safeAlloc.allocDuality,
          idx === 0 ? safeTask.taskDualityCheck : safeAlloc.allocDualityCheck,

          // Items only in first row
          { value: idx === 0 ? safeTask.Items : "", readonly: idx !== 0 },

          // Allocation fields
          { ...safeAlloc.shortName },
          { ...safeAlloc.isin },
          { ...safeAlloc.corpRef },
          { ...safeAlloc.ack },

          // Comments only in first row
          { value: idx === 0 ? safeTask.Comments : "", readonly: idx !== 0 },

          // Metadata
          {
            isChild: idx > 0,
            taskId: task.id || `task-${taskIndex}`,
            allocationId: safeAlloc.id,
            allocationFields: safeTask.allocationFields,
            actions: safeTask.actions
          }
        ];

        result.push(row);
      });
    });

    return result;
  }, [localTasks]);


  /* =====================
     CELL UPDATE FUNCTIONS
  ====================== */
  const updateNestedField = async (taskId, allocationId, parentField, nestedField, value) => {
    // First update local state for immediate UI response
    setLocalTasks(prev => prev.map(task => {
      if (task.id !== taskId) return task;

      // Task-level nested field
      if (!allocationId) {
        return {
          ...task,
          [parentField]: {
            ...task[parentField],
            [nestedField]: value
          }
        };
      }

      // Allocation-level nested field - ID-based update
      return {
        ...task,
        allocations: task.allocations.map(alloc => 
          alloc.id === allocationId 
            ? {
                ...alloc,
                [parentField]: {
                  ...alloc[parentField],
                  [nestedField]: value
                }
              }
            : alloc
        )
      };
    }));

    // Then update Firestore
    try {
      if (!taskId || !country || !date) {
        console.warn("Missing required data for Firestore update:", { taskId, country, date });
        return;
      }

      const task = localTasks.find(t => t.id === taskId);
      if (!task) return;

      let updateData = {};
      
      if (!allocationId) {
        updateData[`${parentField}.${nestedField}`] = value;
      } else {
        const updatedAllocations = task.allocations.map(alloc => 
          alloc.id === allocationId 
            ? {
                ...alloc,
                [parentField]: {
                  ...alloc[parentField],
                  [nestedField]: value
                }
              }
            : alloc
        );
        
        updateData = {
          allocations: updatedAllocations
        };
      }

      updateData.lastModifiedBy = user?.name || "unknown";
      await updateIncomeTask(country, date, taskId, updateData);
      
    } catch (error) {
      console.error("Error updating Firestore:", error);
    }
  };

  const updatePlainTaskField = async (taskId, field, value) => {
    setLocalTasks(prev =>
      prev.map(task =>
        task.id === taskId
          ? { ...task, [field]: value }
          : task
      )
    );

    try {
      if (!country || !date || !taskId) return;
      await updateIncomeTask(country, date, taskId, {
        [field]: value,
        lastModifiedBy: user?.name || "unknown"
      });
    } catch (err) {
      console.error("Failed updating plain task field:", err);
    }
  };

  /* =====================
     CHECKBOX HANDLING
  ====================== */
  const handleCheckboxClick = async (taskId, allocationId, field) => {
    const task = localTasks.find(t => t.id === taskId);
    if (!task) return;

    let current;
    
    if (!allocationId) {
      current = task[field] || EMPTY_CHECK;
    } else {
      const allocation = task.allocations?.find(a => a.id === allocationId);
      if (!allocation) return;
      current = allocation[field] || EMPTY_CHECK;
    }

    if (!current.checked) {
      const newValue = {
        ...current,
        checked: true,
        timestamp: new Date().toISOString(),
        user: user?.name || "Unknown"
      };

      setLocalTasks(prev => prev.map(task => {
        if (task.id !== taskId) return task;

        if (!allocationId) {
          return {
            ...task,
            [field]: newValue
          };
        }

        return {
          ...task,
          allocations: task.allocations.map(a =>
            a.id === allocationId
              ? { ...a, [field]: newValue }
              : a
          )
        };
      }));

      try {
        if (!taskId || !country || !date) return;
        const task = localTasks.find(t => t.id === taskId);
        if (!task) return;

        let updateData = {};
        
        if (!allocationId) {
          updateData[field] = newValue;
        } else {
          const updatedAllocations = task.allocations.map(a =>
            a.id === allocationId
              ? { ...a, [field]: newValue }
              : a
          );
          
          updateData = {
            allocations: updatedAllocations
          };
        }

        updateData.lastModifiedBy = user?.name || "unknown";
        await updateIncomeTask(country, date, taskId, updateData);
      } catch (error) {
        console.error("Error updating checkbox in Firestore:", error);
      }
    } else {
      setCheckboxPopup({
        open: true,
        taskId,
        allocationId,
        field,
        timestamp: current.timestamp,
        user: current.user
      });
    }
  };

  const confirmRemoveCheckbox = async () => {
    const { taskId, allocationId, field } = checkboxPopup;
    const newValue = EMPTY_CHECK;

    setLocalTasks(prev => prev.map(task => {
      if (task.id !== taskId) return task;

      if (!allocationId) {
        return {
          ...task,
          [field]: newValue
        };
      }

      return {
        ...task,
        allocations: task.allocations.map(a =>
          a.id === allocationId
            ? { ...a, [field]: newValue }
            : a
        )
      };
    }));

    try {
      if (!taskId || !country || !date) return;
      const task = localTasks.find(t => t.id === taskId);
      if (!task) return;

      let updateData = {};
      
      if (!allocationId) {
        updateData[field] = newValue;
      } else {
        const updatedAllocations = task.allocations.map(a =>
          a.id === allocationId
            ? { ...a, [field]: newValue }
            : a
        );
        
        updateData = {
          allocations: updatedAllocations
        };
      }

      updateData.lastModifiedBy = user?.name || "unknown";
      await updateIncomeTask(country, date, taskId, updateData);
    } catch (error) {
      console.error("Error removing checkbox timestamp in Firestore:", error);
    }

    setCheckboxPopup({ open: false });
  };

  /* =====================
     ALLOCATION MANAGEMENT
  ====================== */
  const addAllocation = async (taskId) => {
    const newAllocation = {
      id: crypto.randomUUID(),
      allocPrimary: EMPTY_FIELD,
      allocPrimaryCheck: EMPTY_CHECK,
      allocDuality: EMPTY_FIELD,
      allocDualityCheck: EMPTY_CHECK,
      shortName: EMPTY_FIELD,
      isin: EMPTY_FIELD,
      corpRef: EMPTY_FIELD,
      ack: EMPTY_FIELD
    };

    setLocalTasks(prev => prev.map(task => {
      if (task.id !== taskId) return task;
      return {
        ...task,
        allocations: [...(task.allocations || []), newAllocation]
      };
    }));

    try {
      if (!taskId || !country || !date) return;
      const task = localTasks.find(t => t.id === taskId);
      if (!task) return;

      const updateData = {
        allocations: [...(task.allocations || []), newAllocation],
        lastModifiedBy: user?.name || "unknown"
      };
      await updateIncomeTask(country, date, taskId, updateData);
    } catch (error) {
      console.error("Error adding allocation in Firestore:", error);
    }
  };

  const removeAllocation = async (taskId, allocationId) => {
    setLocalTasks(prev => prev.map(task => {
      if (task.id !== taskId) return task;
      return {
        ...task,
        allocations: (task.allocations || []).filter(a => a.id !== allocationId)
      };
    }));

    try {
      if (!taskId || !country || !date) return;
      const task = localTasks.find(t => t.id === taskId);
      if (!task) return;

      const updatedAllocations = (task.allocations || []).filter(a => a.id !== allocationId);
      const updateData = {
        allocations: updatedAllocations,
        lastModifiedBy: user?.name || "unknown"
      };
      await updateIncomeTask(country, date, taskId, updateData);
    } catch (error) {
      console.error("Error removing allocation in Firestore:", error);
    }
  };

  /* =====================
     RENDER CELL - FIXED
  ====================== */
  const renderCell = (cell, col, rowMeta, rowIndex, colIndex) => {
    const { 
      isChild, 
      taskId, 
      allocationId, 
      allocationFields, 
      actions 
    } = rowMeta;
    
    // ACTIONS COLUMN (last column)
    if (col.name === "Actions") {
      return (
        <td
          key={`${rowIndex}-${colIndex}`}
          style={{ width: col.width, backgroundColor: "#fafafa" }}
        >
          <div className="actions-btn-container">
            {/* ➕ ONLY if parent allows AND canAdd is true */}
            {!isChild && actions?.canAdd !== false && (
              <button
                className="shortname-btn add-btn"
                onClick={() => addAllocation(taskId)}
                title="Add allocation"
              >
                +
              </button>
            )}

            {/* ➖ Always allowed on children */}
            {isChild && (
              <button
                className="shortname-btn remove-btn"
                onClick={() => removeAllocation(taskId, allocationId)}
                title="Remove allocation"
              >
                –
              </button>
            )}
          </div>
        </td>
      );
    }

    // Handle checkboxes
    if (col.checkbox) {
      const isPrimaryCheck = colIndex === 3;
      const isDualityCheck = colIndex === 5;
      
      let field = "";
      let isTaskLevel = false;
      
      if (isPrimaryCheck) {
        field = isChild ? "allocPrimaryCheck" : "taskPrimaryCheck";
        isTaskLevel = !isChild;
      } else if (isDualityCheck) {
        field = isChild ? "allocDualityCheck" : "taskDualityCheck";
        isTaskLevel = !isChild;
      }

      return (
        <td 
          key={`${rowIndex}-${colIndex}`} 
          className="checkbox-cell" 
          style={{ width: col.width, backgroundColor: "#fafafa" }}
        >
          <input
            type="checkbox"
            checked={cell?.checked || false}
            onChange={() => handleCheckboxClick(
              taskId,
              isTaskLevel ? null : allocationId,
              field
            )}
          />
        </td>
      );
    }

    // Determine field based on column for non-action, non-checkbox cells
    let field = "";
    let isTaskLevel = false;
    
    switch(col.name) {
      case "Time":
        field = "Time";
        isTaskLevel = true;
        break;
      case "Task":
        field = "Task";
        isTaskLevel = true;
        break;
      case "Primary":
        field = isChild ? "allocPrimary" : "taskPrimary";
        isTaskLevel = !isChild;
        break;
      case "Duality":
        field = isChild ? "allocDuality" : "taskDuality";
        isTaskLevel = !isChild;
        break;
      case "Items":
        field = "Items";
        isTaskLevel = true;
        break;
      case "Short-Name":
        field = "shortName";
        isTaskLevel = false;
        break;
      case "ISIN":
        field = "isin";
        isTaskLevel = false;
        break;
      case "Corp.Ref":
        field = "corpRef";
        isTaskLevel = false;
        break;
      case "ACK":
        field = "ack";
        isTaskLevel = false;
        break;
      case "Comments":
        field = "Comments";
        isTaskLevel = true;
        break;
      default:
        break;
    }

    // Check if this is an allocation field (shortName, isin, corpRef, ack)
    const isAllocField = field === "shortName" || 
                        field === "isin" || 
                        field === "corpRef" || 
                        field === "ack";
    
    // Check if the allocation field is disabled
    const isDisabledAllocField =
      isAllocField &&
      !isChild &&               // 👈 IMPORTANT
      isAllocationFieldDisabled(field, allocationFields);

    
    // Special handling for Time/Task in child rows
    const isTimeOrTask = col.name === "Time" || col.name === "Task";
    const isChildTimeTask = isChild && isTimeOrTask;
    
    // Determine if textarea should be hidden
    const hideTextarea = isChildTimeTask || isDisabledAllocField;
    
    // Determine background color
    const bg = isDisabledAllocField
      ? "#7c7c7cff"
      : isChildTimeTask
        ? "#7c7c7cff"
        : "#fafafa";

    // Render regular cell
    return (
      <td 
        key={`${rowIndex}-${colIndex}`} 
        style={{ width: col.width, backgroundColor: bg }}
      >
        <div className="income-team-cell-wrapper">
          {/* Only show textarea if not hidden and not disabled allocation field */}
          {!hideTextarea ? (
            <textarea
              value={cell?.value ?? ""}
              readOnly={cell?.readonly || isDisabledAllocField}
              disabled={isDisabledAllocField}
              className={`income-team-textarea ${isDisabledAllocField ? 'disabled-cell' : ''}`}
              onChange={(e) => {
                // Prevent editing if readonly or disabled
                if (cell?.readonly || isDisabledAllocField) return;

                if (field.includes("Primary") || field.includes("Duality")) {
                  updateNestedField(
                    taskId,
                    isTaskLevel ? null : allocationId,
                    field,
                    "value",
                    e.target.value
                  );
                  return;
                }

                if (field === "Items" || field === "Comments") {
                  updatePlainTaskField(taskId, field, e.target.value);
                  return;
                }

                updateNestedField(
                  taskId,
                  allocationId,
                  field,
                  "value",
                  e.target.value
                );
              }}
            />
          ) : (
            // Show disabled placeholder for hidden cells
            <div className="income-team-disabled-placeholder">
              {isDisabledAllocField ? "" : ""}
            </div>
          )}
        </div>
      </td>
    );
  };

  /* =====================
     MAIN RENDER
  ====================== */
  return (
    <div className="income-team-container">
      <div className="income-team-content">

        <div className="income-team-table-wrapper">
          <table className="income-team-table">
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    style={{ width: col.width }}
                    className="income-team-header"
                  >
                    {col.name}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="no-data">
                    {localTasks.length === 0 ? (
                      <>
                        <div>No tasks available. Please create a checklist first.</div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          Debug: tasks prop is {tasks ? `present (${tasks.length} items)` : 'null'}
                        </div>
                      </>
                    ) : (
                      <div>Error: Tasks exist but cannot be rendered. Check console for details.</div>
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => {
                  const rowMeta = row[row.length - 1]; // Last item is metadata
                  
                  return (
                    <tr key={rowIndex}>
                      {row.slice(0, columns.length).map((cell, colIndex) => 
                        renderCell(cell, columns[colIndex], rowMeta, rowIndex, colIndex)
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CHECKBOX POPUP */}
      {checkboxPopup.open && (
        <div className="income-team-popup-overlay">
          <div className="income-team-popup-box">
            <h2>Checkbox Details</h2>
            <p><strong>Checked by:</strong> {checkboxPopup.user}</p>
            <p><strong>Time:</strong> {checkboxPopup.timestamp}</p>
            <p style={{ marginTop: "10px" }}>
              Do you want to remove this timestamp?
            </p>
            <div className="income-team-popup-buttons">
              <button
                className="income-team-apply-btn"
                onClick={confirmRemoveCheckbox}
              >
                Yes
              </button>
              <button
                className="income-team-cancel-btn"
                onClick={() => setCheckboxPopup({ open: false })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}